import { getCurrencySymbol } from './accountsHelpers';
import { getRailShortLabel } from './onRampHelpers';

export function formatDepositDate(dateInput) {
  if (!dateInput) return '—';
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function formatDepositAmount(amount, currencyCode) {
  const value = Number(amount) || 0;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
  const symbol = getCurrencySymbol(currencyCode);
  const prefix = value < 0 ? '−' : '+';
  if (symbol === String(currencyCode || '').toUpperCase()) {
    return `${prefix} ${formatted} ${currencyCode}`;
  }
  return `${prefix} ${formatted} ${symbol}`;
}

export function formatDepositAccountType(type) {
  if (!type) return null;
  const raw = String(type).trim();
  if (!raw) return null;

  const compact = raw.replace(/[\s_-]+/g, '').toLowerCase();
  if (compact.includes('sepa')) return 'SEPA';
  if (compact.includes('fedwire')) return 'Fedwire';
  if (compact.includes('fednow')) return 'FedNow';
  if (compact.includes('ach')) return 'ACH';
  if (compact.includes('swift')) return 'SWIFT';
  if (compact.includes('sortcode') || compact.includes('fasterpayment')) return 'Sort code';
  if (compact === 'wire' || compact.endsWith('wire')) return 'Wire';

  const fromRail = getRailShortLabel(raw);
  if (fromRail && fromRail !== 'Bank' && fromRail !== raw) return fromRail;
  if (/^bank$/i.test(raw)) return null;
  return raw;
}

export function getDepositAccountType(deposit) {
  if (!deposit || typeof deposit !== 'object') return null;
  return (
    deposit.accountType ||
    deposit.AccountType ||
    deposit.paymentMethodType ||
    deposit.PaymentMethodType ||
    deposit.paymentMethod ||
    null
  );
}

export function getDepositTitle(deposit) {
  const accountLabel = formatDepositAccountType(getDepositAccountType(deposit));
  const status = String(deposit?.status || deposit?.Status || '').toLowerCase();

  let title = accountLabel || 'Bank';
  if (status === 'pending') title = `${title} · Pending`;
  if (status === 'failed') title = `${title} · Failed`;
  return title;
}

export function isDepositCompleted(deposit) {
  return String(deposit?.status || deposit?.Status || '').toLowerCase() === 'completed';
}

export function isFiatCurrencyCode(currency) {
  const raw = String(currency || '').trim();
  if (!raw || raw.includes('/') || raw.toLowerCase().startsWith('assets')) return false;
  const upper = raw.toUpperCase().replace(/[^A-Z]/g, '');
  if (!upper) return false;
  if (upper === 'USDC' || upper === 'USDT' || upper === 'EURC' || upper === 'EUROC') return true;
  return /^[A-Z]{3}$/.test(upper);
}

export function normalizeDepositCurrency(currency, fallbackCurrency = null) {
  const raw = String(currency || '').trim();
  if (!raw) return fallbackCurrency ? String(fallbackCurrency).toUpperCase() : '';

  const upper = raw.toUpperCase();
  if (isFiatCurrencyCode(raw)) {
    const letters = upper.replace(/[^A-Z]/g, '');
    if (letters === 'USDC' || letters === 'USDT') return 'USD';
    if (letters === 'EURC' || letters === 'EUROC') return 'EUR';
    return letters.slice(0, 3);
  }
  return fallbackCurrency ? String(fallbackCurrency).toUpperCase() : upper;
}

function depositMatchesAccount(deposit, currency, accountProviders = []) {
  const upper = String(currency).toUpperCase();
  const depositCurrency = String(deposit?.currency || deposit?.Currency || '');
  const normalized = normalizeDepositCurrency(depositCurrency, null);

  if (isFiatCurrencyCode(depositCurrency) && normalized === upper) return true;

  if (!isFiatCurrencyCode(depositCurrency) && accountProviders.length > 0) {
    const provider = String(deposit?.provider || deposit?.Provider || '').toLowerCase();
    return accountProviders.some((p) => String(p || '').toLowerCase() === provider);
  }

  return false;
}

export function getDepositsForCurrency(deposits, currency, accountProviders = []) {
  if (!Array.isArray(deposits) || !currency) return [];
  const providers = Array.isArray(accountProviders) ? accountProviders : [];
  return deposits
    .filter((deposit) => depositMatchesAccount(deposit, currency, providers))
    .slice()
    .sort((a, b) => {
      const aTime = new Date(a?.requestedAt || a?.RequestedAt || 0).getTime();
      const bTime = new Date(b?.requestedAt || b?.RequestedAt || 0).getTime();
      return bTime - aTime;
    });
}

export function normalizeDeposit(deposit, fallbackCurrency = null) {
  if (!deposit || typeof deposit !== 'object') return null;
  const accountType = getDepositAccountType(deposit);
  return {
    id: deposit.id || deposit.Id,
    provider: deposit.provider || deposit.Provider,
    amount: Number(deposit.amount ?? deposit.Amount) || 0,
    currency: normalizeDepositCurrency(deposit.currency || deposit.Currency, fallbackCurrency),
    status: deposit.status || deposit.Status,
    requestedAt: deposit.requestedAt || deposit.RequestedAt,
    creditedAt: deposit.creditedAt || deposit.CreditedAt || null,
    accountType,
  };
}
